import { CompositeScreenProps } from '@react-navigation/native'
import { externalLinks, links } from 'constants'
import React from 'react'

import { FollowersFriendsRow } from 'pages/FollowersFriends/subcomponents/FollowersFriendsRow'

import { EmptyState } from 'partials/EmptyState'
import { Page } from 'partials/Page'

import { Box } from 'widgets/Box'
import { FlatList } from 'widgets/FlatList'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { TabView } from 'widgets/TabView'
import { Text } from 'widgets/Text'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useFollowers } from 'hooks/useFollowers'
import { useFollows } from 'hooks/useFollows'
import { useFriends } from 'hooks/useFriends'

import { generateDynamicLink } from 'utils/links'
import { handleShareAction } from 'utils/sharing'

export const FollowersFriendsPage = ({ route, navigation }: CompositeScreenProps<any, any>) => {
  const userId = route.params?.userId
  const initialTab: string = route.params?.tab ?? 'friends'
  const initialTabIndex: number = initialTab === 'friends' || initialTab === 'following' ? 0 : 1

  const { data: currentUser } = useCurrentUser()
  const isCurrentUser = currentUser?.id === userId

  const { data: friends, isLoading: isFriendsLoading } = useFriends(userId)
  const { data: followers, isLoading: isFollowersLoading } = useFollowers(userId)
  const { data: follows, isLoading: isFollowsLoading } = useFollows(userId)

  const handleInviteFriend = async () => {
    const url =
      `${externalLinks.UPSPACE_FULL_PATH}/profile/member/${currentUser?.username}`.toLocaleLowerCase()

    const message = `Check out ${currentUser?.firstName} ${currentUser?.lastName}'s Group profile!`
    handleShareAction(url, message, navigation, links.SHARE_PROFILE_PAGE)
  }

  const navigateToUpspacesPage = () => {
    navigation.navigate(links.TRAINERS_PAGE)
  }

  const friendsTabText = 'You become friends when you both follow each other.'
  const friendsTab = (
    <Box testID='FriendsTab' flex={1} backgroundColor='white'>
      {isFriendsLoading ? (
        <Box flex={1} align='center' justifyContent='center'>
          <LoadingSpinner />
        </Box>
      ) : (
        <Box flex={1}>
          {friends?.length ? (
            <FlatList
              loading={isFriendsLoading}
              data={friends}
              renderItem={({ item }) => (
                <FollowersFriendsRow user={item} isCurrentUser={isCurrentUser} type='friends' />
              )}
              ListHeaderComponent={
                <Box width='100%' padding='lg' align='center' justifyContent='center'>
                  <Text align='center' numberOfLines={2} fontSize='sm' color='greyDark'>
                    {friendsTabText}
                  </Text>
                </Box>
              }
              ListFooterComponent={isFriendsLoading ? <LoadingSpinner /> : null}
            />
          ) : (
            <EmptyState
              title='Friends'
              subtitle={friendsTabText}
              inBetweenMargin='smd'
              actionButtons={[
                {
                  title: 'Invite a friend',
                  type: 'primary',
                  onPress: handleInviteFriend,
                },
              ]}
            />
          )}
        </Box>
      )}
    </Box>
  )

  const followersTab = (
    <Box testID='FollowersTab' flex={1} backgroundColor='white'>
      {isFollowersLoading ? (
        <Box flex={1} align='center' justifyContent='center'>
          <LoadingSpinner />
        </Box>
      ) : (
        <Box flex={1}>
          {followers?.length ? (
            <FlatList
              loading={isFollowersLoading}
              data={followers}
              renderItem={({ item }) => {
                const itemsLength = friends?.filter((f) => f?.id === item?.follower?.id)?.length
                const isFriend = itemsLength !== undefined && itemsLength > 0
                return (
                  <FollowersFriendsRow
                    user={item.follower}
                    isFriend={isFriend}
                    isCurrentUser={isCurrentUser}
                    type='followers'
                  />
                )
              }}
              ListHeaderComponent={
                <Box width='100%' padding='lg' align='center' justifyContent='center'>
                  <Text align='center' numberOfLines={2} fontSize='sm' color='greyDark'>
                    Your followers are all users who follow you.
                  </Text>
                </Box>
              }
              ListFooterComponent={isFollowersLoading ? <LoadingSpinner /> : null}
            />
          ) : (
            <EmptyState
              title='Followers'
              subtitle='Invite your people and get sharing.'
              inBetweenMargin='smd'
              actionButtons={[
                {
                  title: 'Invite a friend',
                  type: 'primary',
                  onPress: handleInviteFriend,
                },
              ]}
            />
          )}
        </Box>
      )}
    </Box>
  )

  const followingTab = (
    <Box testID='FollowingTab' flex={1} backgroundColor='white'>
      {isFollowsLoading ? (
        <Box flex={1} align='center' justifyContent='center'>
          <LoadingSpinner />
        </Box>
      ) : (
        <Box flex={1}>
          {follows?.length ? (
            <FlatList
              loading={isFollowsLoading}
              data={follows}
              renderItem={({ item }) => (
                <FollowersFriendsRow
                  user={item.trainer}
                  isCurrentUser={isCurrentUser}
                  type='following'
                />
              )}
              ListHeaderComponent={
                <Box width='100%' padding='lg' align='center' justifyContent='center'>
                  <Text align='center' numberOfLines={2} fontSize='sm' color='greyDark'>
                    Your following are all of the people that you follow.
                  </Text>
                </Box>
              }
              ListFooterComponent={isFollowsLoading ? <LoadingSpinner /> : null}
            />
          ) : (
            <EmptyState
              title='Following'
              subtitle='Find new and exciting people to follow.'
              inBetweenMargin='smd'
              actionButtons={[
                {
                  title: 'Discover',
                  type: 'primary',
                  onPress: navigateToUpspacesPage,
                },
              ]}
            />
          )}
        </Box>
      )}
    </Box>
  )

  return (
    <Page withBack withNav title='Community' backgroundColor='white' navigation={navigation}>
      <TabView
        testID='FollowersFriendsTabView'
        tabs={['Friends', 'Followers', 'Following']}
        initialTabIndex={initialTabIndex}
      >
        {friendsTab}
        {followersTab}
        {followingTab}
      </TabView>
    </Page>
  )
}
